import React, { useEffect, useMemo, useState } from "react";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import {
  getGenderDistribution,
  getAgeDistribution,
} from "../../../../../services/dashboardServices";

import {
  FaChild,
  FaRedo,
  FaUsers,
  FaVenusMars,
} from "react-icons/fa";

const GENDER_COLORS = {
  Masculin: "#4F46E5",
  Féminin: "#EC4899",
  "Non renseigné": "#94A3B8",
};

const AGE_COLORS = {
  "-18": "#06B6D4",
  "18+": "#10B981",
  "Non renseigné": "#94A3B8",
};

function extractArray(data) {
  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.data)) return data.data;
  }

  return [];
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("fr-FR");
}

function getPercent(value, total) {
  if (!total) return 0;
  return Math.round((Number(value || 0) / total) * 1000) / 10;
}

function normalizeGender(value) {
  const gender = String(value || "").trim();

  if (!gender) return "Non renseigné";
  if (gender.toLowerCase().startsWith("masc")) return "Masculin";
  if (gender.toLowerCase().startsWith("fém") || gender.toLowerCase().startsWith("fem")) return "Féminin";

  return gender;
}

function normalizeAge(value) {
  const age = String(value || "").trim();

  if (!age) return "Non renseigné";
  if (age === "-18" || age.includes("18") && age.includes("-")) return "-18";
  if (age === "18+" || age.includes("+")) return "18+";

  return age;
}

function prepareGenderData(rows) {
  const base = {
    Masculin: 0,
    Féminin: 0,
    "Non renseigné": 0,
  };

  extractArray(rows).forEach((item) => {
    const name = normalizeGender(item?.genre || item?.name);
    const value = Number(item?.count ?? item?.value ?? 0);

    if (name in base) {
      base[name] += value;
    } else {
      base["Non renseigné"] += value;
    }
  });

  return Object.entries(base)
    .map(([name, value]) => ({
      name,
      value,
      color: GENDER_COLORS[name] || GENDER_COLORS["Non renseigné"],
    }))
    .filter((item) => item.value > 0);
}

function prepareAgeData(rows) {
  const base = {
    "-18": 0,
    "18+": 0,
    "Non renseigné": 0,
  };

  extractArray(rows).forEach((item) => {
    const name = normalizeAge(item?.age || item?.name);
    const value = Number(item?.count ?? item?.value ?? 0);

    if (name in base) {
      base[name] += value;
    } else {
      base["Non renseigné"] += value;
    }
  });

  return Object.entries(base)
    .map(([name, value]) => ({
      name,
      value,
      color: AGE_COLORS[name] || AGE_COLORS["Non renseigné"],
    }))
    .filter((item) => item.value > 0);
}

function DashboardMiniStat({ icon, label, value, helper, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    pink:
      "border-pink-200 bg-pink-50 text-pink-700 dark:border-pink-500/20 dark:bg-pink-500/10 dark:text-pink-300",
    cyan:
      "border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-500/20 dark:bg-cyan-500/10 dark:text-cyan-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
  };

  return (
    <div className={`rounded-2xl border px-4 py-3 ${accents[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>

        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
            {label}
          </p>

          <p className="mt-0.5 text-xl font-bold text-slate-950 dark:text-white">
            {value}
          </p>

          {helper ? (
            <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
              {helper}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, total }) {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload;
  const value = Number(row?.value || 0);
  const percent = getPercent(value, total);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 text-sm shadow-xl dark:border-slate-700 dark:bg-slate-900">
      <p className="font-bold text-slate-900 dark:text-white">{row?.name}</p>

      <p className="mt-2 text-slate-700 dark:text-slate-300">
        Adhérents : <span className="font-bold">{formatNumber(value)}</span>
      </p>

      <p className="mt-1 rounded-xl bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
        {percent}% du total
      </p>
    </div>
  );
}

function DonutBlock({ title, subtitle, data, total }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/60">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-base font-bold text-slate-900 dark:text-white">
            {title}
          </h4>

          <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
            {subtitle}
          </p>
        </div>

        <span className="rounded-2xl bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {formatNumber(total)}
        </span>
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-[220px_1fr]">
        <div className="h-[210px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="58%"
                outerRadius="82%"
                paddingAngle={2}
              >
                {data.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>

              <Tooltip content={<ChartTooltip total={total} />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="flex flex-col justify-center gap-2">
          {data.map((item) => {
            const percent = getPercent(item.value, total);

            return (
              <div
                key={item.name}
                className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-900"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="h-3 w-3 shrink-0 rounded-full"
                    style={{ background: item.color }}
                  />

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-bold text-slate-900 dark:text-white">
                        {item.name}
                      </span>

                      <span className="shrink-0 text-sm font-bold text-slate-900 dark:text-white">
                        {formatNumber(item.value)}
                      </span>
                    </div>

                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${percent}%`,
                          background: item.color,
                        }}
                      />
                    </div>
                  </div>

                  <span className="w-12 shrink-0 text-right text-xs font-bold text-slate-500 dark:text-slate-400">
                    {percent}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="h-5 w-72 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
          <div className="mt-2 h-3 w-96 max-w-full animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
        </div>

        <div className="h-10 w-40 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[...Array(4)].map((_, index) => (
          <div
            key={index}
            className="h-20 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800"
          />
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="h-[300px] animate-pulse rounded-[24px] bg-slate-200 dark:bg-slate-800" />
        <div className="h-[300px] animate-pulse rounded-[24px] bg-slate-200 dark:bg-slate-800" />
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-950/50">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
        <FaVenusMars />
      </div>

      <h3 className="mt-4 text-lg font-bold text-slate-900 dark:text-white">
        Aucune donnée à afficher
      </h3>

      <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
        Aucune répartition genre ou âge n’a été trouvée pour la saison active.
      </p>
    </div>
  );
}

function ErrorPanel({ message, onRetry }) {
  return (
    <div className="rounded-[24px] border border-rose-200 bg-rose-50 p-5 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
      <p className="font-bold">Erreur de chargement</p>

      <p className="mt-1 text-sm leading-6">{message}</p>

      <button
        type="button"
        onClick={onRetry}
        className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-rose-700"
      >
        <FaRedo />
        Réessayer
      </button>
    </div>
  );
}

export default function GenderAgeDistributionChart() {
  const [genderData, setGenderData] = useState([]);
  const [ageData, setAgeData] = useState([]);

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      setErrorMsg("");

      const [genderResponse, ageResponse] = await Promise.all([
        getGenderDistribution(),
        getAgeDistribution(),
      ]);

      setGenderData(prepareGenderData(genderResponse));
      setAgeData(prepareAgeData(ageResponse));
    } catch (error) {
      console.error("Erreur répartition genre/âge :", error);
      setGenderData([]);
      setAgeData([]);
      setErrorMsg("Impossible de charger la répartition genre et âge.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const totals = useMemo(() => {
    const genderTotal = genderData.reduce(
      (sum, item) => sum + Number(item.value || 0),
      0
    );

    const ageTotal = ageData.reduce(
      (sum, item) => sum + Number(item.value || 0),
      0
    );

    const total = genderTotal || ageTotal || 0;

    const masculin =
      genderData.find((item) => item.name === "Masculin")?.value || 0;
    const feminin =
      genderData.find((item) => item.name === "Féminin")?.value || 0;
    const minors = ageData.find((item) => item.name === "-18")?.value || 0;
    const adults = ageData.find((item) => item.name === "18+")?.value || 0;

    return {
      total,
      masculin,
      feminin,
      minors,
      adults,
      feminineRate: getPercent(feminin, genderTotal),
      minorsRate: getPercent(minors, ageTotal),
    };
  }, [genderData, ageData]);

  const hasData =
    genderData.some((item) => item.value > 0) ||
    ageData.some((item) => item.value > 0);

  if (loading) return <LoadingSkeleton />;

  if (errorMsg) {
    return <ErrorPanel message={errorMsg} onRetry={fetchData} />;
  }

  if (!hasData) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
              <FaVenusMars />
            </span>

            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Répartition genre & âge
              </h3>

              <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                Indicateurs utiles pour le suivi des adhérents et les dossiers
                institutionnels.
              </p>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={fetchData}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <FaRedo className={loading ? "animate-spin" : ""} />
          Actualiser
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <DashboardMiniStat
          icon={<FaUsers />}
          label="Total"
          value={formatNumber(totals.total)}
          helper="Adhérents analysés"
          accent="indigo"
        />

        <DashboardMiniStat
          icon={<FaVenusMars />}
          label="Féminines"
          value={`${totals.feminineRate}%`}
          helper={`${formatNumber(totals.feminin)} féminines`}
          accent="pink"
        />

        <DashboardMiniStat
          icon={<FaChild />}
          label="-18 ans"
          value={`${totals.minorsRate}%`}
          helper={`${formatNumber(totals.minors)} mineurs`}
          accent="cyan"
        />

        <DashboardMiniStat
          icon={<FaUsers />}
          label="18 ans et +"
          value={formatNumber(totals.adults)}
          helper="Adhérents majeurs"
          accent="emerald"
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <DonutBlock
          title="Masculin / Féminin"
          subtitle="Répartition globale par genre."
          data={genderData}
          total={genderData.reduce((sum, item) => sum + item.value, 0)}
        />

        <DonutBlock
          title="-18 ans / 18 ans et +"
          subtitle="Répartition globale par âge."
          data={ageData}
          total={ageData.reduce((sum, item) => sum + item.value, 0)}
        />
      </div>

      <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-600 dark:border-slate-800 dark:bg-slate-950/50 dark:text-slate-300">
        <p>
          Ce bloc peut servir pour les demandes de subvention : part des
          féminines, nombre de mineurs, nombre de majeurs et volume total
          d’adhérents.
        </p>
      </div>
    </div>
  );
}